import { useTranslation } from 'react-i18next'

import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material'

import OfflineBoltIcon from '@mui/icons-material/OfflineBolt'

import CurrencyUtils from '@utils/currency'
import {
  ISelectableInterval,
  getSubscriptionLabelFromPlanPeriod,
} from '@utils/plan-detail'

import {
  contentWrapperSx,
  customIconSx,
  customPlanContainerSx,
  highestPaymentFrequenciesContainerSx,
  highestPaymentFrequencySx,
  loadingSx,
  loadingWrapperSx,
  otherPaymentFrequenciesContainerSx,
  paymentFrequencySx,
  perMonthSx,
  planButtonSx,
  planWrapperSx,
} from './styles'

interface IPlanPricingProps {
  alias: string
  paymentPeriods?: ISelectableInterval[]
  isHighlighted?: boolean
  isCustomPlan?: boolean
  isAvailable?: boolean
  isLoading?: boolean
  hasPromotionCode?: boolean
  planSetWidth: number
  onClick?: () => void
}

const PlanPricing = ({
  alias,
  paymentPeriods,
  isHighlighted,
  isCustomPlan,
  isAvailable,
  isLoading,
  hasPromotionCode,
  planSetWidth,
  onClick,
}: IPlanPricingProps) => {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <Box sx={planWrapperSx({ isHighlighted, isLoading, planSetWidth })}>
        <Box sx={loadingWrapperSx} data-testid="loading-indicator">
          <CircularProgress sx={loadingSx({ isHighlighted })} />
        </Box>
      </Box>
    )
  }

  const highestPaymentFrequency = paymentPeriods?.[0]

  const otherPaymentFrequencies = Array.from({ length: 2 }).map(
    (_, index) => paymentPeriods?.[index + 1]
  )

  const getPlanButtonText = () => {
    if (!isAvailable) {
      return t('Coming Soon!')
    }

    if (hasPromotionCode) {
      return t('See discounted price')
    }

    return t('Choose Plan')
  }

  return (
    <Box sx={planWrapperSx({ isHighlighted, isCustomPlan, planSetWidth })}>
      <Box sx={contentWrapperSx}>
        <Typography variant="h5">{alias}</Typography>
        {isCustomPlan ? (
          <>
            <OfflineBoltIcon sx={customIconSx({ isHighlighted })} />
            <Stack sx={customPlanContainerSx}>
              <Typography variant="subtitle1">{t('Custom Plan')}</Typography>
            </Stack>
          </>
        ) : (
          <>
            <Stack sx={highestPaymentFrequenciesContainerSx}>
              <Typography variant="h3" sx={highestPaymentFrequencySx}>
                {CurrencyUtils.toCurrencyFormat(
                  highestPaymentFrequency?.price ?? 0,
                  {
                    emptySpace: false,
                  }
                )}
              </Typography>
              <Typography
                data-testid="plan-frequency-label"
                variant="subtitle2"
                sx={perMonthSx}
              >
                {t('Per {{label}}', {
                  label: getSubscriptionLabelFromPlanPeriod({
                    planPeriod: highestPaymentFrequency?.period,
                  }),
                })}
              </Typography>
            </Stack>
            {otherPaymentFrequencies
              .filter((v) => (v?.price ?? 0) > 0)
              .map((v, index) => (
                <Stack
                  key={`payment-frequency-${index + 1}`}
                  sx={otherPaymentFrequenciesContainerSx}
                >
                  <Typography variant="h5" sx={paymentFrequencySx}>
                    {CurrencyUtils.toCurrencyFormat(v?.price ?? 0, {
                      emptySpace: false,
                    })}
                  </Typography>
                  <Typography
                    data-testid="plan-frequency-label"
                    variant="subtitle2"
                    sx={perMonthSx}
                  >
                    {t('Per {{label}}', {
                      label: getSubscriptionLabelFromPlanPeriod({
                        planPeriod: v?.period,
                      }),
                    })}
                  </Typography>
                </Stack>
              ))}
          </>
        )}
      </Box>

      <Button
        data-testid="plan-button"
        size="large"
        variant="outlined"
        sx={planButtonSx({
          isHighlighted,
          isAvailable,
        })}
        onClick={onClick}
      >
        {getPlanButtonText()}
      </Button>
    </Box>
  )
}

export { PlanPricing }
